import { createFileRoute } from "@tanstack/react-router";
import { physicianPageSchema, safeJsonLd } from "@/lib/structured-data-core";
import { lazy, Suspense, useState } from "react";
import { User, GraduationCap, X } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { SocialPosts } from "@/components/site/SocialPosts";
import hero from "@/assets/restore/cloninger-hero.png.asset.json";
import signature from "@/assets/restore/cloninger-signature.png.asset.json";
import playButton from "@/assets/restore/play.png.asset.json";
import g1 from "@/assets/restore/cloninger-g1.webp.asset.json";
import g2 from "@/assets/restore/cloninger-g2.webp.asset.json";
import g3 from "@/assets/restore/cloninger-g3.webp.asset.json";
import g4 from "@/assets/restore/cloninger-g4.webp.asset.json";
import g5 from "@/assets/restore/cloninger-g5.webp.asset.json";
import { primaryVideoObject } from "@/lib/video-seo";
import { PosterVideo } from "@/components/site/PosterVideo";
import { absoluteUrl } from "@/lib/absolute-url";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
import { QuoteBand } from "@/components/site/QuoteBand";
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

export const Route = createFileRoute("/doctors/dr-adam-cloninger")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/doctors/dr-adam-cloninger/" }],
    meta: [
      { title: "Dr. Adam Cloninger - Hair Transplant Doctor & Surgeon" },
      {
        name: "description",
        content:
          "Dr. Adam Cloninger is a RESTORE Hair Doctor in the Chicagoland area. He is trained by Dr. James Harris and is one of the leading surgeons.",
      },
      { property: "og:title", content: "Dr. Adam Cloninger — Hair Restoration Physician" },
      {
        property: "og:description",
        content:
          "Meet Dr. Adam Cloninger, co-lead of RESTORE® Chicago and a distinguished hair restoration surgeon.",
      },
      { property: "og:image", content: absoluteUrl(hero.url) },
      { name: "twitter:image", content: absoluteUrl(hero.url) },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd(primaryVideoObject("/doctors/dr-adam-cloninger/")),
      },
      {
        type: "application/ld+json",
        children: safeJsonLd(
          physicianPageSchema({
            name: "Dr. Adam Cloninger",
            jobTitle: "Hair Restoration Physician",
            description: "Dr. Adam Cloninger is co-lead of RESTORE® Chicago and was trained by Dr. James Harris. He is board-certified in internal medicine.",
            image: hero.url,
            pageUrl: "https://restorehair.com/doctors/dr-adam-cloninger/",
            clinic: {
              street: "1415 22nd St, #950",
              city: "Oak Brook",
              state: "IL",
            },
          }),
        ),
      },
    ],
  }),
  component: DrCloningerPage,
});

const GALLERY = [g1.url, g2.url, g3.url, g4.url, g5.url];
const VIDEO_URL =
  "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/Restore_DocBio_Cloninger2022_V1.mp4";

function DrCloningerPage() {
  const [tab, setTab] = useState<"bio" | "education">("bio");
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main data-cta-location="doctor">
        {/* Hero */}
        <section className="overflow-hidden bg-background">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-4 px-4 pt-6 lg:grid-cols-2 lg:items-end lg:gap-4 lg:pt-0">
            <div className="pb-6 lg:py-10">
              <img
                src={signature.url}
                alt="Adam Cloninger signature"
                className="mb-4 h-12 w-auto opacity-70"
              />
              <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] lg:whitespace-nowrap font-bold tracking-tight text-foreground">
                Dr. Adam Cloninger
              </h1>

              <p className="mt-3 text-lg md:text-xl font-semibold text-primary">
                Hair Restoration Physician
              </p>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground">
                Recognized as a distinguished hair surgeon by peers &amp; patients, Dr. Adam
                Cloninger is co-lead of the RESTORE<sup>®</sup> Chicago office, trained by
                RESTORE medical director, Dr James Harris. Esteemed for his contributions in
                the field of cardiology, Dr Cloninger is board-certified in internal medicine,
                and holds a doctorate in medicine and a bachelor's degree in biology. His
                interest in hair restoration started with his own hair loss while in his late
                20s and guides his commitment as a surgeon to deliver the level of care and
                results that he would expect for himself.
              </p>
            </div>
            <div className="relative flex justify-center lg:justify-end lg:self-end">
              <img
                src={hero.url}
                alt="Dr. Adam Cloninger — Hair Restoration Physician"
                className="block h-[440px] w-full object-cover object-top md:h-[500px] lg:h-[600px]"
                loading="eager"
              />
              {/* In the server HTML for Google video indexing; the play button still opens the player. */}
              <PosterVideo
                src={VIDEO_URL}
                poster={hero.url}
                title="Meet Dr. Adam Cloninger"
                fit="cover"
                className="object-top"
              />
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                aria-label="Play Dr. Adam Cloninger's video"
                title="Play Video"
                className="absolute left-1/2 top-[65%] -translate-x-1/2 -translate-y-1/2 transition hover:opacity-90"
              >
                <img
                  src={playButton.url}
                  alt="Play video"
                  className="h-24 w-24 object-contain md:h-28 md:w-28"
                />
              </button>
            </div>
          </div>
        </section>
        <QuoteBand />

        {videoOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/80 p-4"
            role="dialog"
            aria-modal="true"
            onClick={() => setVideoOpen(false)}
          >
            <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setVideoOpen(false)}
                aria-label="Close video"
                className="absolute -top-10 right-0 text-background"
              >
                <X size={28} />
              </button>
              <video src={VIDEO_URL} controls autoPlay className="w-full" />
            </div>
          </div>
        )}

        {/* Bio / Education tabs */}
        <section className="bg-muted/40 py-12 md:py-16">
          <div className="mx-auto w-full max-w-6xl px-4">
            <div className="grid max-w-3xl grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => setTab("bio")}
                className={`inline-flex items-center justify-center gap-2 px-6 py-5 text-lg font-semibold transition ${
                  tab === "bio"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-foreground shadow-sm hover:bg-card/80"
                }`}
              >
                <User size={20} />
                Full Bio
              </button>
              <button
                type="button"
                onClick={() => setTab("education")}
                className={`inline-flex items-center justify-center gap-2 px-6 py-5 text-lg font-semibold transition ${
                  tab === "education"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-foreground shadow-sm hover:bg-card/80"
                }`}
              >
                <GraduationCap size={20} />
                Education
              </button>
            </div>

            <div className="bg-card px-4 py-10 md:px-10 md:py-14 shadow-sm">
              <div hidden={tab !== "bio"}>
                <div className="mx-auto max-w-6xl">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                      Dr. Adam Cloninger's Bio
                    </h2>
                    <div className="mt-4 space-y-4 text-base leading-relaxed text-muted-foreground">
                      <p>
                        Recognized as a distinguished hair surgeon by peers &amp; patients, Dr.
                        Adam Cloninger is co-lead of RESTORE's Chicago office, trained by
                        RESTORE medical director, Dr James Harris. Esteemed for his
                        contributions in the field of cardiology, Dr Cloninger is
                        board-certified in internal medicine, and holds a doctorate in
                        medicine and a bachelor's degree in biology. His interest in hair
                        restoration started with his own hair loss while in his late 20s and
                        guides his commitment as a surgeon to deliver the level of care and
                        results that he would expect for himself.
                      </p>
                      <p>
                        As co-lead of RESTORE's surgical team, Dr. Cloninger applies his own
                        hair loss journey along with his experience as a hair surgeon, to
                        uphold the RESTORE 'Standards of Care' established by Dr. Harris. He
                        has performed hundreds of successful hair restoration surgeries and
                        his reviews speak to his prestigious reputation for extraordinary
                        attention-to-detail and overall excellence.
                      </p>
                      <p>
                        Cloninger believes he's most effective because of the collective
                        contributions of the entire team — "Every member of our medical staff
                        undergoes rigorous training and evaluation by Dr. Harris and the
                        co-leads. We are the industry's leading surgical team, period. At
                        RESTORE, choosing the right doctor is no longer a concern."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div hidden={tab !== "education"}>
                <div className="mx-auto max-w-6xl">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                    Education &amp; Speciality
                  </h2>
                  <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                    <div className="rounded-none border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Doctor of Medicine (MD)
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">2017</p>
                      <p className="mt-2 text-sm text-muted-foreground">Ross University</p>
                      <p className="text-sm text-muted-foreground">
                        North Brunswick Township, New Jersey
                      </p>
                    </div>
                    <div className="rounded-none border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Bachelor of Science in Biology
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        Pre-medical 2009
                      </p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        Palm Beach Atlantic University
                      </p>
                      <p className="text-sm text-muted-foreground">West Palm Beach, FL</p>
                    </div>
                    <div className="rounded-none border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Post Graduate Training
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        2018 – 2021
                      </p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        Ochsner Clinic Foundation
                      </p>
                      <p className="text-sm text-muted-foreground">New Orleans, LA</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Photo gallery */}
        <section className="bg-background py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-center text-2xl md:text-3xl font-bold text-foreground">
              Learn More About <span className="text-primary">Dr. Cloninger</span>
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {GALLERY.map((url, i) => (
                <img
                  key={url}
                  src={url}
                  alt={`Dr. Adam Cloninger at work ${i + 1}`}
                  loading="lazy"
                  className="w-full rounded-md object-cover"
                />
              ))}
            </div>
          </div>
        </section>
        {/* WordPress copy: the bio paragraph under "Learn More About Dr. Cloninger". */}
        <section className="bg-background pb-12 md:pb-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">Bio</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">{"Before joining RESTORE’s elite surgical team, Dr. Adam Cloninger received his bachelor’s degree in Biology from Palm Beach Atlantic University and moved on to earn his Doctorate of Medicine from Ross University. He went on to complete a residency in Internal Medicine at Ochsner Clinic Foundation in New Orleans and subsequently received his board certification. His area of interest has been in the field of cardiology where he has authored papers and has been published multiple times. He has presented on an array of topics in the field of cardiology in various settings including national medical conferences. He is especially proud of his volunteer medical mission work in South America. Dr. Cloninger was personally selected by the world renowned hair surgeon, author and inventor, Dr. James Harris who has trained and mentored him and now co-leads RESTORE’s Chicago office."}</p>
          </div>
        </section>

        <Suspense fallback={null}>
          <Results resultsHref="https://restorehair.com/results/" resultsButtonLabel="See All Before & Afters" />
          <Reviews />
        </Suspense>
        <SocialPosts />
        <Suspense fallback={null}>
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
